import React, { Component } from "react";
import PropTypes from "prop-types";
import LoadingFallback from "./LoadingFallback";
import ErrorBoundary from "./ErrorBoundary";

class LazyComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      Component: null,
      loading: true,
      error: null,
      isInView: false,
    };
    this.containerRef = React.createRef();
    this.observer = null;
  }

  componentDidMount() {
    // If lazy loading is disabled, load immediately
    if (!this.props.lazy) {
      this.loadComponent();
      return;
    }

    // Setup intersection observer for lazy loading
    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection,
        {
          rootMargin: this.props.rootMargin || "100px 0px",
          threshold: this.props.threshold || 0.1,
        }
      );

      if (this.containerRef.current) {
        this.observer.observe(this.containerRef.current);
      }
    } else {
      // Fallback for browsers without IntersectionObserver
      this.loadComponent();
    }
  }

  componentWillUnmount() {
    if (this.observer && this.containerRef.current) {
      this.observer.unobserve(this.containerRef.current);
    }
  }

  handleIntersection = (entries) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
      this.setState({ isInView: true });
      this.loadComponent();
      // Stop observing once component is loaded
      if (this.observer) {
        this.observer.unobserve(entry.target);
      }
    }
  };

  loadComponent = async () => {
    try {
      this.setState({ loading: true, error: null });
      
      const componentModule = await this.props.loader();
      const Component = componentModule.default || componentModule;
      
      this.setState({ 
        Component, 
        loading: false,
        isInView: true 
      });
    } catch (error) {
      console.error("Failed to load component:", error);
      this.setState({ 
        error, 
        loading: false 
      });
    }
  };

  render() {
    const { Component, loading, error, isInView } = this.state;
    const { 
      fallback, 
      errorFallback, 
      lazy,
      minHeight,
      ...componentProps 
    } = this.props;

    // Show placeholder until component is in view (for lazy loading)
    if (lazy && !isInView) {
      return (
        <div 
          ref={this.containerRef}
          style={{ minHeight: minHeight || "200px" }}
        >
          {fallback || <LoadingFallback variant="minimal" />}
        </div>
      );
    }

    // Loading state
    if (loading) {
      return (
        <div style={{ minHeight: minHeight || "200px" }}>
          {fallback || <LoadingFallback />}
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div style={{ minHeight: minHeight || "200px" }}>
          {errorFallback || (
            <ErrorBoundary 
              title="Bileşen Yüklenemedi"
              message="Bu bölüm yüklenirken bir hata oluştu."
              onRetry={this.loadComponent}
            />
          )}
        </div>
      );
    }

    // Render the loaded component
    if (Component) {
      return (
        <ErrorBoundary>
          <Component {...componentProps} />
        </ErrorBoundary>
      );
    }

    return null;
  }
}

LazyComponent.propTypes = {
  loader: PropTypes.func.isRequired,
  lazy: PropTypes.bool,
  fallback: PropTypes.node,
  errorFallback: PropTypes.node,
  rootMargin: PropTypes.string,
  threshold: PropTypes.number,
  minHeight: PropTypes.string,
};

LazyComponent.defaultProps = {
  lazy: true,
  rootMargin: "100px 0px",
  threshold: 0.1,
};

export default LazyComponent;


// WEBPACK FOOTER //
// ./src/components/shared/LazyComponent.js